<template>
  <div class="toggle-sidebar-container def-sidebar">

    <!-- Toggle Button -->
    <button
      type="button"
      :aria-label="$t('sidebar.ariaLabel.menu')"
      :aria-expanded="String(isSidebarOpen)"
      aria-controls="sidebar"
      v-b-toggle.sidebar
      @click="toggleSidebar"
      class="toggle-sidebar-btn"
    >
      <font-awesome-icon v-if="!isSidebarOpen" :icon="['fas', 'bars']" aria-hidden="true" />
      <font-awesome-icon v-else :icon="['fas', 'times']" aria-hidden="true" />
    </button>

    <!-- Sidebar -->
    <b-sidebar id="sidebar"
               class="sidebar-drawer"
               bg-variant="light"
               shadow
               backdrop
               no-header
               no-header-close
               :aria-label="$t('sidebar.ariaLabel.sidebar')"
               @shown="onSidebarShown"
               @hidden="onSidebarHidden">

      <div class="sidebar-content d-flex flex-column h-100">

        <div class="sidebar-header text-center">
          <img :src="logoPath" alt="Logo" class="sidebar-logo img-fluid mb-4">
        </div>

        <!-- Navigation Links -->
        <nav class="sidebar-nav text-left flex-grow-1" :aria-label="$t('sidebar.ariaLabel.sidebar')">
          <b-list-group flush class="sidebar-nav-list">
            <b-list-group-item
              v-for="item in getLinks()"
              :key="item.key"
              :class="['sidebar-nav-item', { activeItem: item.isCurrent }]"
              button
              @click="item.action()"
              :role="item.role||'link'"
              :aria-current="item.isCurrent&&'page'||null"
            >
            <span class="icon-wrapper" role="presentation">
              <font-awesome-icon :icon="['fas', item.icon]"/>
            </span>
            {{ item.name() }}
            </b-list-group-item>
          </b-list-group>
        </nav>

        <!--<div class="sidebar-footer mt-auto ">-->
        <div class="sidebar-footer mt-auto d-flex" id="btnLanguages">
          <Button v-for="entry in config.LANGUAGE_LIST" :key="entry.value" v-on:click="changeLanguage(entry.value)" :aria-label="entry.nativeSelectText" v-bind:aria-pressed="isLanguageActive(entry.value) ? 'true' : 'false'" :lang="entry.title">
            {{entry.title}}
          </Button>
        </div>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
  import {accountService} from "@/services/api/accountService";
  import {useLocaleStore} from "../../stores/locale";
  import {useAppStore} from "../../stores/app";
  import {useAuthStore} from "../../stores/auth";
  import {Mode, Workflow} from "@/constants/constants";
  import Button from "@/components/actions/Button.vue";
  import {canAccessRouteName} from "@/utils/authRouteUtils";

  /** @typedef {{key:number,name:function():string,action:function():never,icon:string,routeId?:string,visible?:function():boolean,role?:string}} linkItem */
  export default {
    components: {
      Button
      // ToggleButtonGroup
    },
    data() {
      return {
        isSidebarOpen: false,
        /** @type {linkItem[]} */
        items:[
          //   todo: load from $router
          {key: 1,name: ()=>this.$t('system.title.dashboard'), action: this.navigateToDashboard, icon: 'home', routeId: 'Dashboard'},
          {key: 2,name: ()=>this.$t('customerCard.title'), action: this.navigateToCustomerCard, icon: 'qrcode', routeId: 'CustomerCard', visible: ()=>this.canAccessRoute('CustomerCard')},
          {key: 3,name: ()=>this.$t('account.title'), action: this.navigateToAccount, icon: 'user', routeId: 'Account', visible: ()=>this.canAccessRoute('Account')},
          {key: 4,name: ()=>this.$t('orderMenu.title'), action: this.navigateToOrder, icon: 'burger', routeId: 'OrderMenuParameters', visible: ()=>this.canAccessRoute('OrderMenuParameters')},
          {key: 5,name: ()=>this.$t('mealPlan.title'), action: this.navigateToMealPlan, icon: 'calendar-days', routeId: 'MealPlanParameters', visible: ()=>this.canAccessRoute('MealPlanParameters')},
          {key: 6,name: ()=>this.$t('orderHistory.title'), action: this.navigateToOrderHistory, icon: 'rectangle-list', routeId: 'OrderHistory', visible: ()=>this.canAccessRoute('OrderHistory')},
          {key: 7,name: ()=>this.$t('transactionHistory.title'), action: this.navigateToTransactionHistory, icon: 'receipt', routeId: 'TransactionHistory', visible: ()=>this.canAccessRoute('TransactionHistory')},
          {key: 8,name: ()=>this.$t('studentAccounts.title'), action: this.navigateToStudentAccounts, icon: 'user-graduate', routeId: 'StudentAccounts', visible: ()=>this.canAccessRoute('StudentAccounts')},
          {key: 9,name: ()=>this.$t('system.button.logout'), action: this.logout, icon: 'power-off'},
        ],
      };
    },
    computed: {
      logoPath() {
        return process.env.BASE_URL + 'settings/resources/logos/logo.png';
      },
      config() {
        return window.config;
      },
      languageOptions() {
        const list = (this.config && this.config.LANGUAGE_LIST) ? this.config.LANGUAGE_LIST : [];
        return list.map((entry) => ({
          value: entry.value,
          label: entry.title,
          ariaLabel: entry.nativeSelectText,
          attr: {'lang':entry.title},
        }));
      },
      currentLanguage: {
        get() {
          return this.$i18n && this.$i18n.locale;
        },
        set(newLocale) {
          this.changeLanguage(newLocale);
        },
      },
    },
    setup() {
      const authStore = useAuthStore();
      const localeStore = useLocaleStore();
      const appStore = useAppStore();

      return {
        authStore,
        localeStore,
        appStore
      };
    },
    methods: {
      canAccessRoute(routeName) {
        return canAccessRouteName(this.authStore, this.config, routeName);
      },
      async changeLanguage(newLocale) {
        await this.localeStore.setLocale(newLocale);
      },
      toggleSidebar() {
        this.isSidebarOpen = !this.isSidebarOpen;
      },
      onSidebarShown() {
        this.isSidebarOpen = true;
      },
      onSidebarHidden() {
        this.isSidebarOpen = false;
      },
      navigateToAccount() {
        this.$navigateToAccount();
      },
      navigateToOrder() {
        this.appStore.setWorkflow(Workflow.ORDER);
        if (this.appStore.isMode(Mode.TABLE) || this.appStore.isMode(Mode.TAKEAWAY)) {
          this.$navigate(this.appStore.orderEntryRouteName || this.appStore.tableOrderEntryRouteName || 'OrderMenuPlanner', { withReplace: true });
          return;
        }
        this.$navigateToOrderMenuParameters();
      },
      navigateToMealPlan() {
        this.appStore.setWorkflow(Workflow.MEAL_PLAN);
        this.$navigateToMealPlanParameters();
      },
      navigateToCustomerCard() {
        this.appStore.setWorkflow(Workflow.TOP_UP);
        this.$navigateToCustomerCard();
      },
      navigateToTransactionHistory() {
        this.$navigateToTransactionHistory();
      },
      navigateToOrderHistory() {
        this.$navigateToOrderHistory();
      },
      navigateToStudentAccounts() {
        this.appStore.setWorkflow(Workflow.SCHOOL);
        this.$navigateToStudentAccounts();
      },
      navigateToDashboard() {
        this.$navigateToDashboard();
      },
      isLanguageActive(language)
      {
        return this.$i18n.locale === language;
      },
      isLoggedin() {
        return this.authStore.isLoggedIn;
      },
      /** @returns {Array<linkItem & {isCurrent:boolean}>} */
      getLinks() {
        if (!this.isLoggedin()){
          let _self = this;
          return ([{key:0,name:()=>this.$t('button.login'),action:_self.goToLogin,icon:'power-off',routeId:'Login'}]).map(a=>({...a,isCurrent:_self.isCurrent(a)}));
        }

        return this.$data.items.filter(function(item){
          return item.visible === undefined || item.visible();
        }).map(a=>({...a,isCurrent:this.isCurrent(a)}));
      },
      /** @param {linkItem} item */
      isCurrent(item){
        if (typeof item.routeId == 'string') {
          return this.$route.name === item.routeId;
        }
        return false;
      },
      goToLogin(){
        this.$navigateToLogin();
      },
      async logout() {
        const result = await accountService.logout();
        if (result.error) {
          this.makeToast('danger', result.message);
        } else {
          this.makeToast('success', this.$t('notification.logoutSuccessful'));
        }

        await this.$nextTick(); // Wait for the DOM to update
        this.authStore.$reset();
        this.$navigateToLogin();
      }
    },
  };
</script>

<style scoped lang="scss">
  .toggle-sidebar-container {
    position: unset;
    display: flex;
    align-items: center;
  }

  ::v-deep .b-sidebar {
    top: Max(10vh,55px);
    position: absolute;
    max-height: unset;
    height: calc(100dvh - Max(10vh,55px));
  }

  ::v-deep .b-sidebar-outer {
    top: 0;
    position: absolute;
  }

  ::v-deep .b-sidebar-backdrop {
    position: absolute;
    bottom: 0;
    right: 0;
    width: unset;
    height: 100dvh;
  }

  .sidebar-footer {
    align-items: baseline;
  }

  .sidebar-footer .btn {
    border: 0;
    &:focus-visible,
    &:hover,
    &:focus { /* layout issue with overlapping elements */
      z-index: 1;
    }
  }


  .icon-wrapper {
    display: inline-block;
    width: 30px; /* Setze die Breite auf einen Wert, der für alle Icons passt */
    text-align: center;
  }
</style>
